import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";

type SeoRoute = {
  title: string;
  description: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown>;
};

const SITE_NAME = "Foil Bloom";
/** Served from `public/foilbloom-og.png`; shared previews need an absolute URL. */
const OG_IMAGE_PATH = "/foilbloom-og.png";
const DEFAULT_TITLE = "Foil Bloom | MTG Deck Builder";
const DEFAULT_DESCRIPTION =
  "Build and analyze Magic: The Gathering decks with AI-powered recommendations.";

const PUBLIC_ROUTE_META: Record<string, SeoRoute> = {
  "/": {
    title: "Foil Bloom | MTG Deck Builder",
    description:
      "Build smarter MTG decks with card search, collection-aware generation, and AI analysis.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: "/",
    },
  },
  "/pricing": {
    title: "Pricing | Foil Bloom",
    description: "Compare Free and Pro plans for Foil Bloom deck building features.",
  },
  "/about": {
    title: "About | Foil Bloom",
    description:
      "Learn about Foil Bloom and how it helps players design better MTG decks faster.",
  },
  "/downloads": {
    title: "Downloads | Foil Bloom",
    description: "Download data packs and supporting resources for Foil Bloom.",
  },
  "/community": {
    title: "Community Decks | Foil Bloom",
    description: "Explore and share public MTG decks from the Foil Bloom community.",
  },
  "/login": {
    title: "Log In | Foil Bloom",
    description: "Sign in to access your saved decks, collection, and account features.",
    noindex: true,
  },
  "/register": {
    title: "Create Account | Foil Bloom",
    description: "Create a Foil Bloom account to save decks and unlock personalized tools.",
    noindex: true,
  },
};

const PRIVATE_ROUTE_META: Record<string, SeoRoute> = {
  "/build": {
    title: "Build Deck | Foil Bloom",
    description: "Generate MTG decklists using your selected constraints and preferences.",
    noindex: true,
  },
  "/analyze": {
    title: "Analyze Deck | Foil Bloom",
    description: "Analyze deck performance, curve, and improvement suggestions.",
    noindex: true,
  },
  "/collection": {
    title: "Collection | Foil Bloom",
    description: "Manage your card collection and use it in collection-aware deck building.",
    noindex: true,
  },
  "/decks": {
    title: "Saved Decks | Foil Bloom",
    description: "Browse and manage your saved deck builds.",
    noindex: true,
  },
  "/bloomy": {
    title: "Bloomy Chat | Foil Bloom",
    description: "Ask Bloomy about your collection, saved decks, and MTG card knowledge.",
    noindex: true,
  },
  "/account": {
    title: "Account | Foil Bloom",
    description: "Manage your Foil Bloom account, plan, and billing settings.",
    noindex: true,
  },
  "/admin": {
    title: "Admin | Foil Bloom",
    description: "Administrative tools for Foil Bloom.",
    noindex: true,
  },
};

function upsertMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    if (property) {
      tag.setAttribute("property", name);
    } else {
      tag.setAttribute("name", name);
    }
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(url: string) {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", "canonical");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", url);
}

function upsertJsonLd(payload: Record<string, unknown> | null) {
  const existing = document.head.querySelector<HTMLScriptElement>(
    'script[data-seo="route-jsonld"]',
  );
  if (!payload) {
    existing?.remove();
    return;
  }
  const tag = existing ?? document.createElement("script");
  tag.setAttribute("type", "application/ld+json");
  tag.setAttribute("data-seo", "route-jsonld");
  tag.textContent = JSON.stringify(payload);
  if (!existing) {
    document.head.appendChild(tag);
  }
}

function resolveRouteMeta(pathname: string): SeoRoute | null {
  if (PUBLIC_ROUTE_META[pathname]) return PUBLIC_ROUTE_META[pathname];
  if (PRIVATE_ROUTE_META[pathname]) return PRIVATE_ROUTE_META[pathname];
  if (pathname.startsWith("/community/")) {
    return {
      title: "Community Deck Details | Foil Bloom",
      description: "Inspect the full public decklist, Arena import text, and build actions.",
    };
  }
  if (pathname.startsWith("/decks/")) {
    return {
      title: "Saved Deck Details | Foil Bloom",
      description: "View a saved deck's rationale, decklist, and Arena import text.",
      noindex: true,
    };
  }
  if (pathname.startsWith("/admin")) return PRIVATE_ROUTE_META["/admin"];
  return null;
}

export function SeoManager() {
  const { pathname } = useLocation();
  const { firebaseUser } = useAuth();

  useEffect(() => {
    const routeMeta = resolveRouteMeta(pathname);
    const title = routeMeta?.title ?? "Page Not Found | Foil Bloom";
    const description = routeMeta?.description ?? "The requested Foil Bloom page does not exist.";
    const isPrivateSurface =
      pathname.startsWith("/admin") ||
      pathname.startsWith("/build") ||
      pathname.startsWith("/analyze") ||
      pathname.startsWith("/collection") ||
      pathname.startsWith("/decks") ||
      pathname.startsWith("/account");
    const shouldNoIndex =
      !!routeMeta?.noindex || isPrivateSurface || (pathname === "/" && !!firebaseUser);

    const canonical = new URL(pathname, window.location.origin).toString();
    const ogTitle = title || DEFAULT_TITLE;
    const ogDescription = description || DEFAULT_DESCRIPTION;
    const ogImageUrl = new URL(OG_IMAGE_PATH, window.location.origin).toString();

    document.title = ogTitle;
    upsertCanonical(canonical);
    upsertMeta("description", ogDescription);
    upsertMeta("robots", shouldNoIndex ? "noindex, nofollow" : "index, follow");
    upsertMeta("og:title", ogTitle, true);
    upsertMeta("og:description", ogDescription, true);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:url", canonical, true);
    upsertMeta("og:site_name", SITE_NAME, true);
    upsertMeta("og:image", ogImageUrl, true);
    upsertMeta("og:image:alt", `${SITE_NAME} — MTG deck builder`, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", ogTitle);
    upsertMeta("twitter:description", ogDescription);
    upsertMeta("twitter:image", ogImageUrl);

    const routeJsonLd = routeMeta?.jsonLd
      ? {
          ...routeMeta.jsonLd,
          url: canonical,
        }
      : null;
    upsertJsonLd(routeJsonLd);
  }, [pathname, firebaseUser]);

  return null;
}
